'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { Network } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
}

interface WhatIsPlanSectionProps {
    plan: Plan;
}

export function WhatIsPlanSection({ plan }: WhatIsPlanSectionProps) {
    const t = useTranslations('planDetails.monolinePlan.whatIsPlan');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    return (
        <SectionWrapper ref={sectionRef.ref} variant="gradient" padding="md" className="relative overflow-hidden">
            {/* Enhanced decorative background elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/10 blur-3xl animate-pulse" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-gradient-to-r from-primary-500/5 to-blue-500/5 blur-3xl" />
            </div>

            <div className="container relative z-10">
                <div className="max-w-6xl mx-auto">
                    {/* Title Section */}
                    <div className={cn(
                        "text-center mb-16 transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    )}>
                        <Typography variant="h2" className="mb-6 text-gray-900 dark:text-white">
                            {t('title')}
                        </Typography>
                    </div>

                    {/* Enhanced Content Card */}
                    <div className={cn(
                        "max-w-4xl mx-auto transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
                    )} style={{ transitionDelay: '200ms' }}>
                        <div className="relative group">
                            {/* Floating Icon Badge */}
                            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 flex items-center justify-center shadow-2xl z-20 group-hover:scale-110 transition-transform duration-300">
                                <Network className="w-10 h-10 text-white" />
                            </div>

                            {/* Decorative corner accent */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/20 to-transparent rounded-tr-2xl"></div>

                            {/* Main Card */}
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-10 pr-16 md:pr-20 border-2 border-primary-200 dark:border-primary-800 shadow-2xl hover:shadow-3xl hover:border-primary-400 dark:hover:border-primary-600 transition-all duration-300 overflow-hidden group-hover:-translate-y-1">
                                {/* Subtle background pattern */}
                                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 rounded-full blur-3xl"></div>
                                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500 rounded-full blur-3xl"></div>
                                </div>

                                {/* Left border accent with gradient */}
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 via-primary-400 to-blue-500"></div>

                                <div className="relative z-10 space-y-6">
                                    <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg md:text-xl">
                                        {t('description1')}
                                    </Typography>
                                    <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg md:text-xl">
                                        {t('description2')}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

